import React, {createContext, useCallback, useContext, useMemo} from 'react';
import {assert} from 'assert-ts';
import type {Data} from 'schemaDefinition/types/values';
import {mapMergeSuggestionType} from 'services/mergeSuggestions';
import {getActualDataChangesWithSchema} from 'services/metadata';
import {useSnacks} from 'components/snacks';
import {useSchemaCodelists} from 'schema';
import {validate} from 'schema/form/functions/validators';
import {useSchema} from 'schemas';
import type {CompareEntitiesContextProps, CompareEntitiesType} from '../types';

export const CompareEntitiesContext = createContext<CompareEntitiesType>({
  target: {
    data: {},
    schema: {
      name: 'target',
      parts: [],
    },
  },
  source: undefined,
  onChange: () => undefined,
  onSave: () => Promise.resolve(),
  type: 'SERIES',
});

export const useCompareEntitiesContextProviderValue = ({
  targetData,
  sourceData,
  schemaRef,
  onSave,
}: CompareEntitiesContextProps): CompareEntitiesType => {
  const schema = useSchema(schemaRef);
  const codelistMap = useSchemaCodelists(schema);
  const {errorSnack, warningSnack} = useSnacks();

  const {changesData, changesSchema} = useMemo(() => {
    const changes =
      targetData && sourceData
        ? getActualDataChangesWithSchema(
            sourceData,
            targetData,
            schema,
            undefined,
            {
              excludeEmpty: true,
              excludeEqual: true,
              excludeReadOnly: false,
            },
          )
        : undefined;

    return {
      changesData: changes?.actualChanges,
      changesSchema: changes?.trimmedSchema,
    };
  }, [sourceData, targetData, schema]);

  const [editData, setEditData] = React.useState<Data>(targetData);

  const onChange = useCallback((data: Data) => {
    if (data) {
      setEditData(data);
    } else {
      setEditData({});
    }
  }, []);

  const handleSave = useCallback(() => {
    if (editData) {
      const validation = validate(
        editData,
        undefined,
        schema,
        assert(codelistMap, 'CompareEntitiesContext: codelistMap is required'),
      );

      if (validation.valid !== 'error') {
        if (validation.valid === 'warning') {
          warningSnack(validation.errors.map(e => e.value).join(', '));
        }

        return onSave(editData, sourceData);
      }

      if (validation.errors.filter(e => e.value).length > 0) {
        errorSnack(validation.errors.map(e => e.value).join(', '));
      }
    }

    return Promise.reject();
  }, [
    codelistMap,
    editData,
    errorSnack,
    onSave,
    sourceData,
    schema,
    warningSnack,
  ]);

  return useMemo(() => {
    return {
      type: mapMergeSuggestionType(schemaRef),
      onChange,
      onSave: handleSave,
      target: {
        data: editData,
        schema: schema,
      },
      source:
        changesData && changesSchema
          ? {
              data: changesData,
              schema: changesSchema,
            }
          : undefined,
    };
  }, [
    schemaRef,
    onChange,
    handleSave,
    editData,
    schema,
    changesData,
    changesSchema,
  ]);
};

export const useCompareEntitiesContext = () => {
  return assert(
    useContext(CompareEntitiesContext),
    'CompareEntitiesContext: context expected',
  );
};
